/**
 * Static key → finger mapping for QWERTY layout.
 *
 * Finger IDs (0–9, left→right):
 *   0  left pinky
 *   1  left ring
 *   2  left middle
 *   3  left index
 *   4  left thumb   (Space)
 *   5  right thumb  (Space)
 *   6  right index
 *   7  right middle
 *   8  right ring
 *   9  right pinky
 *
 * Canonical names used for CSS classes and labels.
 */

export type FingerId = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export interface FingerInfo {
  id: FingerId;
  name: string;
  hand: 'left' | 'right';
  /** Short label shown on key overlay */
  short: string;
}

export const FINGERS: Record<FingerId, FingerInfo> = {
  0: { id: 0, name: 'Left Pinky',   hand: 'left',  short: 'LP' },
  1: { id: 1, name: 'Left Ring',    hand: 'left',  short: 'LR' },
  2: { id: 2, name: 'Left Middle',  hand: 'left',  short: 'LM' },
  3: { id: 3, name: 'Left Index',   hand: 'left',  short: 'LI' },
  4: { id: 4, name: 'Left Thumb',   hand: 'left',  short: 'LT' },
  5: { id: 5, name: 'Right Thumb',  hand: 'right', short: 'RT' },
  6: { id: 6, name: 'Right Index',  hand: 'right', short: 'RI' },
  7: { id: 7, name: 'Right Middle', hand: 'right', short: 'RM' },
  8: { id: 8, name: 'Right Ring',   hand: 'right', short: 'RR' },
  9: { id: 9, name: 'Right Pinky',  hand: 'right', short: 'RP' },
};

/**
 * Maps every heatmap-tracked key to a finger.
 * Space is tracked under ' ' but triggered by either thumb — use left thumb (4) as canonical.
 * Keys not in this map (e.g. function keys) have no finger highlight.
 */
export const KEY_FINGER: Readonly<Record<string, FingerId>> = {
  // ── Number row ──────────────────────────────────────────────────────────────
  '`': 0, '1': 0, '2': 1, '3': 2, '4': 3, '5': 3,
  '6': 6, '7': 6, '8': 7, '9': 8, '0': 9, '-': 9, '=': 9,

  // ── Top row (QWERTY) ────────────────────────────────────────────────────────
  'q': 0, 'w': 1, 'e': 2, 'r': 3, 't': 3,
  'y': 6, 'u': 6, 'i': 7, 'o': 8, 'p': 9, '[': 9, ']': 9, '\\': 9,

  // ── Home row ────────────────────────────────────────────────────────────────
  'a': 0, 's': 1, 'd': 2, 'f': 3, 'g': 3,
  'h': 6, 'j': 6, 'k': 7, 'l': 8, ';': 9, "'": 9,

  // ── Bottom row ──────────────────────────────────────────────────────────────
  'z': 0, 'x': 1, 'c': 2, 'v': 3, 'b': 3,
  'n': 6, 'm': 6, ',': 7, '.': 8, '/': 9,

  // ── Special ─────────────────────────────────────────────────────────────────
  ' ': 4,
  'Tab': 0,
  'Enter': 9,
  'Backspace': 9,
};

/** CSS class suffix for each finger, used on .heatmap-key elements */
export const FINGER_CLASS: Record<FingerId, string> = {
  0: 'f-l-pinky',
  1: 'f-l-ring',
  2: 'f-l-middle',
  3: 'f-l-index',
  4: 'f-l-thumb',
  5: 'f-r-thumb',
  6: 'f-r-index',
  7: 'f-r-middle',
  8: 'f-r-ring',
  9: 'f-r-pinky',
};
